import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import Datastore from "../../communication/Datastore";
import { REHYDRATE } from "redux-persist";
import store from "../../state/Store";
import LogEntry from "../../types/system/LogEntry";
import PageSpecification from "../../types/system/PageSpecification";
import { unwrappedDispatch } from "../../foundation/Dispatching";

// Define the type for our state store entry
type LogState = {
    allData: Record<string, Record<number, LogEntry[]>>;
    pageData: LogEntry[];
    virtualSpec: LogPageSpecification;
    internalSpec: Record<string, LogPageSpecification>;
    status: string;
    error: string;
};

// Define the initial state for our state store entry
const initialState: LogState = {
    allData: {},
    pageData: [],
    virtualSpec: {
        page: 0,
        size: 20,
        total: 0,
    } as LogPageSpecification,
    internalSpec: {},
    status: "idle",
    error: null,
};

// Create a slice to hold all our reducers
const logSlice = createSlice({
    name: "log",
    initialState: initialState,
    reducers: {
        setVirtualSpecType: (state, action: PayloadAction<string>) => {
            state.virtualSpec = {
                ...state.virtualSpec,
                type: action.payload,
                page: 0,
            };
        },
        addInternalSpecification: (
            state,
            action: PayloadAction<LogPageSpecification>,
        ) => {
            if (!state.internalSpec[action.payload.type])
                state.internalSpec[action.payload.type] = {
                    type: action.payload.type,
                    types: action.payload.types,
                    page: -1,
                    size: 200,
                    total: 0,
                } as LogPageSpecification;
        },
        setVirtualPageAndSize: (
            state,
            action: PayloadAction<LogPageSpecification>,
        ) => {
            state.virtualSpec.type = action.payload.type;
            state.virtualSpec.page = action.payload.page;
            state.virtualSpec.size = action.payload.size;
            state.virtualSpec.total =
                state.internalSpec[action.payload.type].total;

            const internalPageIndex = Math.floor(
                (state.virtualSpec.page * state.virtualSpec.size) /
                    state.internalSpec[action.payload.type].size,
            );
            const internalPage =
                state.allData[action.payload.type]?.[internalPageIndex];
            const translatedPageStart =
                state.virtualSpec.page * state.virtualSpec.size -
                internalPageIndex *
                    state.internalSpec[action.payload.type].size;
            const translatedPageEnd =
                translatedPageStart + state.virtualSpec.size;

            state.pageData =
                internalPage?.slice(
                    Math.min(translatedPageStart, internalPage.length),
                    Math.min(translatedPageEnd, internalPage.length),
                ) ?? [];

            state.virtualSpec.waiting =
                state.virtualSpec.loading && state.pageData.length === 0;
        },
    },
    extraReducers(builder) {
        builder
            .addCase(REHYDRATE, (_, rawData: any) => {
                if (rawData?.key !== "tts") return undefined;

                const allData = {};

                if (rawData?.payload?.logs?.allData)
                    for (let entry in rawData?.payload?.logs.allData) {
                        allData[entry] = {};

                        for (let entrySet in rawData?.payload?.logs.allData[
                            entry
                        ])
                            allData[entry][entrySet] =
                                rawData?.payload?.logs.allData[entry][
                                    entrySet
                                ].map(item => new LogEntry(item));
                    }

                return {
                    allData: allData,
                    pageData:
                        rawData?.payload?.logs?.pageData?.map(
                            item => new LogEntry(item),
                        ) ?? [],
                    virtualSpec:
                        rawData?.payload?.logs?.virtualSpec ??
                        ({
                            page: 0,
                            size: 20,
                            total: 0,
                        } as PageSpecification),
                    internalSpec: rawData?.payload?.logs?.internalSpec ?? {},
                    status: "idle",
                    error: null,
                };
            })
            .addCase(fetchLogPage.pending, (state, action) => {
                state.status = "loading";
                state.virtualSpec.loading = true;
                Object.keys(state.internalSpec).forEach(
                    key => (state.internalSpec[key].loading = true),
                );
            })
            .addCase(fetchLogPage.fulfilled, (state, action) => {
                state.status = "succeeded";
                // Add any fetched posts to the array
                state.virtualSpec.total = action.payload[2];
                state.internalSpec[action.payload[0]].total = action.payload[2];
                state.virtualSpec.loading = false;
                Object.keys(state.internalSpec).forEach(
                    key => (state.internalSpec[key].loading = true),
                );
                state.allData[action.payload[0]] =
                    state.allData[action.payload[0]] ?? {};
                state.allData[action.payload[0]][action.payload[1]] =
                    action.payload[3].map(item => new LogEntry(item));

                const internalPageIndex = Math.floor(
                    (state.virtualSpec.page * state.virtualSpec.size) /
                        state.internalSpec[action.payload[0]].size,
                );
                const internalPage =
                    state.allData[action.payload[0]][internalPageIndex];
                const translatedPageStart =
                    state.virtualSpec.page * state.virtualSpec.size -
                    internalPageIndex *
                        state.internalSpec[action.payload[0]].size;
                const translatedPageEnd =
                    translatedPageStart + state.virtualSpec.size;

                state.pageData =
                    internalPage?.slice(
                        Math.min(translatedPageStart, internalPage.length),
                        Math.min(translatedPageEnd, internalPage.length),
                    ) ?? [];

                state.virtualSpec.waiting =
                    state.virtualSpec.loading && state.pageData.length === 0;
            })
            .addCase(fetchLogPage.rejected, (state, action) => {
                state.status = "failed";
                state.virtualSpec.loading = false;
                Object.keys(state.internalSpec).forEach(
                    key => (state.internalSpec[key].loading = false),
                );
                state.error = action.error.message;
            });
    },
});

const { setVirtualSpecType, setVirtualPageAndSize } = logSlice.actions;

export const { addInternalSpecification } = logSlice.actions;

interface LoadArgs {
    type: string;
    fetchIndex: number;
}
export class LogPageSpecification implements PageSpecification {
    page: number;
    size: number;
    total: number;
    loading: boolean;
    waiting: boolean;
    type: string;
    types: string[];
}

// This is a thunk, they are used to load the state
const fetchLogPage = createAsyncThunk(
    "log/fetchLogPage",
    async (loadArgs: LoadArgs, thunkAPI) => {
        const { logs } = store.getState().db;
        const internalSpec = logs.internalSpec[loadArgs.type];
        const response = await Promise.all([
            Datastore.postType<number>(`api/Logs/Count`, internalSpec.types),
            Datastore.postType<LogEntry[]>(
                `api/Logs/Page/${loadArgs.fetchIndex}/Size/${internalSpec.size}`,
                internalSpec.types,
            ),
        ]);
        const result = [
            loadArgs.type,
            loadArgs.fetchIndex,
            response[0],
            response[1],
        ] as [string, number, number, LogEntry[]];
        return result;
    },
);
const evaluatePrivateData = createAsyncThunk(
    "log/evaluatePrivateData",
    async (specification: LogPageSpecification, thunkAPI): Promise<void> => {
        const { logs } = store.getState().db;
        const internalSpec = logs.internalSpec[specification.type];

        if (!internalSpec) return;

        const requiredInternalPage = Math.max(
            Math.ceil(
                ((specification.page + 1) * specification.size - 1) /
                    internalSpec.size,
            ) - 1,
            0,
        );

        if (logs.status !== "loading") {
            if (
                !logs.allData[specification.type] ||
                !logs.allData[specification.type][requiredInternalPage]
            ) {
                await thunkAPI.dispatch(
                    fetchLogPage({
                        type: specification.type,
                        fetchIndex: requiredInternalPage,
                    } as LoadArgs),
                );
            } else {
                const direction =
                    specification.page > logs.virtualSpec.page
                        ? 1
                        : specification.page < logs.virtualSpec.page
                        ? -1
                        : 0;

                if (direction !== 0) {
                    const pageGap =
                        direction > 0
                            ? (internalSpec.size -
                                  (specification.page * specification.size -
                                      requiredInternalPage *
                                          internalSpec.size)) /
                              specification.size
                            : (specification.page * specification.size -
                                  requiredInternalPage * internalSpec.size) /
                              specification.size;

                    if (pageGap < 4) {
                        if (
                            direction < 0 &&
                            requiredInternalPage > 0 &&
                            !logs.allData[specification.type][
                                requiredInternalPage - 1
                            ]
                        ) {
                            await thunkAPI.dispatch(
                                fetchLogPage({
                                    type: specification.type,
                                    fetchIndex: requiredInternalPage - 1,
                                }),
                            );
                        } else if (
                            direction > 0 &&
                            !logs.allData[specification.type][
                                requiredInternalPage + 1
                            ]
                        ) {
                            await thunkAPI.dispatch(
                                fetchLogPage({
                                    type: specification.type,
                                    fetchIndex: requiredInternalPage + 1,
                                }),
                            );
                        }
                    }
                }
            }
        }
    },
);
export const getLogPage = createAsyncThunk(
    "log/getLogPage",
    async (type: string, thunkAPI): Promise<void> => {
        thunkAPI.dispatch(setVirtualSpecType(type));
        const { logs } = store.getState().db;
        thunkAPI.dispatch(setVirtualPageAndSize(logs.virtualSpec));
        await unwrappedDispatch(evaluatePrivateData(logs.virtualSpec));
    },
);
export const setLogPageAndSize = createAsyncThunk(
    "log/setLogPageAndSize",
    async (specification: LogPageSpecification, thunkAPI) => {
        await unwrappedDispatch(evaluatePrivateData(specification));
        thunkAPI.dispatch(setVirtualPageAndSize(specification));
    },
);

// We have to export the reducer as this is added to the state store
export default logSlice.reducer;
